/**
 * Field/entity DISPLAY resolver — derives the visual presentation of an inferred
 * field/entity from its canonical attributes (role, refinements, sensitivity), so
 * the renderers don't hand-code an attribute→visual branch per signal.
 *
 * Sensitivity CLASSIFICATION is shared from @inferrex/inference-schema (the same
 * classifier the backend governance view uses — one source, no drift). This module
 * owns only the DISPLAY layer: the role→hue/emphasis/lock treatment (codegen'd in
 * field-display-rules.generated.ts) + the single hue→token-class map.
 */
import type { InferredField, InferredEntity, SensitivityBasis } from '@inferrex/inference-schema';
import { explainFieldSensitivity, sensitivityFromRefinement, type SensitivityClass } from '@inferrex/inference-schema';
import { ROLE_DISPLAY, SENSITIVITY_DISPLAY } from './field-display-rules.generated.js';

/** The full display-hue vocabulary. Owned HERE (the consumer) so HUE_CLASS stays
 * exhaustive; the generated display rules are typed against these. */
export type DisplayHue =
  | 'neutral'
  | 'info'
  | 'success'
  | 'warning'
  | 'danger'
  | 'identifier'
  | 'accent'
  | 'sensitive';
export interface RoleDisplay {
  readonly hue: DisplayHue;
  readonly emphasis?: 'identifier' | 'sensitive';
  readonly lock?: boolean;
}

/** The ONE place hue tokens map to (semantic) token classes — never raw palette
 * values. A generated rule yields a hue; this turns it into a className. */
export const HUE_CLASS: Record<DisplayHue, string> = {
  neutral: 'bg-muted text-muted-foreground',
  info: 'bg-info-bg text-info-foreground',
  success: 'bg-success-bg text-success-foreground',
  warning: 'bg-warning-bg text-warning-foreground',
  danger: 'bg-destructive-bg text-destructive',
  identifier: 'bg-ftype-indigo-bg text-ftype-indigo-fg',
  accent: 'bg-ftype-violet-bg text-ftype-violet-fg',
  sensitive: 'bg-warning-bg text-warning-foreground',
};

export interface DisplayBadge {
  readonly label: string;
  readonly className: string;
  readonly title?: string;
}

export interface FieldDisplaySpec {
  /** Semantic role chip (the field's MEANING), already hued. */
  readonly roleBadge?: DisplayBadge;
  /** Refinement chips (pii-scanner etc.), hued by derived sensitivity. */
  readonly refinementBadges: readonly DisplayBadge[];
  /** The field's data-sensitivity class (a person's decision, else role + refinements + name default). */
  readonly sensitivity?: SensitivityClass;
  /** Why it has that class — or, with no class, that a person cleared it. */
  readonly sensitivityBasis?: SensitivityBasis;
  /** The class as a chip, saying where it came from. Absent for a non-sensitive field. */
  readonly sensitivityBadge?: DisplayBadge;
  /** Row-level treatment. */
  readonly emphasis?: 'identifier' | 'sensitive';
  /** Show a lock affordance (credential-class). */
  readonly lock: boolean;
}

export interface EntityDisplaySpec {
  readonly sensitiveFieldCount: number;
  readonly emphasis?: 'sensitive';
}

function humanise(s: string): string {
  return s.replace(/[_-]+/g, ' ').trim();
}

/** Minimal refinement shape the badge resolver reads (matches the canonical
 * `RefinementResult` without coupling to the generated model type). */
export interface RefinementLike {
  readonly value: string;
  readonly confidence?: number | undefined;
  readonly layer: number;
}

/** Turn an open refinement map into hued badges — sensitivity-bearing refinements
 * (pii-scanner, etc.) get the sensitive hue from the shared classifier; the rest are
 * neutral. Shared by resolveFieldDisplay + the RefinementChips component (one source
 * for "is this refinement sensitive?", replacing the old inline regex).
 *
 * `alreadyShown` names values the card already shows in another badge. A neutral refinement that
 * repeats one is dropped: the entity-namer's "organization" sat beside an "organization" archetype
 * on most Salesforce entities (2026-10-09). A sensitivity-bearing one is always kept, for its hue. */
export function resolveRefinementBadges(
  refinements: Readonly<Record<string, RefinementLike>> | undefined,
  alreadyShown: readonly (string | undefined)[] = [],
): DisplayBadge[] {
  if (!refinements) return [];
  const shown = new Set(alreadyShown.filter((v): v is string => !!v).map((v) => humanise(v).toLowerCase()));
  const badges: DisplayBadge[] = [];
  for (const [key, r] of Object.entries(refinements)) {
    const sens = sensitivityFromRefinement(key, r.value);
    if (!sens && shown.has(humanise(r.value).toLowerCase())) continue;
    const hue: DisplayHue = sens ? SENSITIVITY_DISPLAY[sens].hue : 'neutral';
    const pct = typeof r.confidence === 'number' ? ` · ${Math.round(r.confidence * 100)}%` : '';
    badges.push({
      label: humanise(r.value),
      className: HUE_CLASS[hue],
      title: `${humanise(key)} · L${r.layer}${pct}`,
    });
  }
  return badges;
}

/** Chip label per sensitivity class — PII is an acronym; the rest read as words. */
function sensitivityLabel(s: SensitivityClass): string {
  return s === 'pii' ? 'PII' : humanise(s);
}

const SENSITIVITY_BASIS_TITLE: Record<SensitivityBasis, string> = {
  'person': 'Set by your reviewer',
  'name-default': 'Treated as sensitive by default: tax, VAT, national and company-registration IDs can identify a person. Change it with Correct.',
  'classifier': 'Classified from the field\'s role and the PII scanner. Change it with Correct.',
};

export function resolveFieldDisplay(field: InferredField): FieldDisplaySpec {
  const role = field.role ? ROLE_DISPLAY[field.role] : undefined;
  const { sensitivity, basis: sensitivityBasis } = explainFieldSensitivity(field);
  const refinementBadges = resolveRefinementBadges(field.refinements);
  const sensitivityBadge: DisplayBadge | undefined = sensitivity && sensitivityBasis
    ? {
        label: sensitivityLabel(sensitivity),
        className: HUE_CLASS[SENSITIVITY_DISPLAY[sensitivity].hue],
        title: SENSITIVITY_BASIS_TITLE[sensitivityBasis],
      }
    : undefined;

  const lock = !!role?.lock || (sensitivity ? SENSITIVITY_DISPLAY[sensitivity].lock : false);
  const emphasis: 'identifier' | 'sensitive' | undefined =
    role?.emphasis ?? (sensitivity ? 'sensitive' : undefined);

  const roleBadge: DisplayBadge | undefined = field.role && role
    ? {
        label: humanise(field.role),
        className: HUE_CLASS[role.hue],
        title: field.roleConfidence != null
          ? `role confidence ${Math.round(field.roleConfidence * 100)}%`
          : 'semantic role',
      }
    : undefined;

  return {
    refinementBadges,
    lock,
    ...(roleBadge ? { roleBadge } : {}),
    ...(sensitivity ? { sensitivity } : {}),
    ...(sensitivityBasis ? { sensitivityBasis } : {}),
    ...(sensitivityBadge ? { sensitivityBadge } : {}),
    ...(emphasis ? { emphasis } : {}),
  };
}

/** The role vocabulary a person can pick from, in a stable order (every classification role has a
 *  display treatment, so the generated table IS the vocabulary). */
export const FIELD_ROLE_OPTIONS: readonly string[] = Object.keys(ROLE_DISPLAY).sort();

/** A role's human label. */
export function roleLabel(role: string): string {
  return humanise(role);
}

export function resolveEntityDisplay(entity: InferredEntity): EntityDisplaySpec {
  let sensitiveFieldCount = 0;
  for (const f of entity.fields) {
    if (resolveFieldDisplay(f).sensitivity) sensitiveFieldCount++;
  }
  return {
    sensitiveFieldCount,
    ...(sensitiveFieldCount > 0 ? { emphasis: 'sensitive' as const } : {}),
  };
}

// ── Captured example values (audit §1.3) ──────────────────────────────────────
// Every discovery reader captures `exampleValues` and, until §Q.2b task 5, nothing
// rendered them — the one signal that answers "is this the field I think it is?"
// faster than any type badge. The DECISION of whether to show them is a display
// concern derived from the same sensitivity classifier as the lock affordance, so it
// lives here beside resolveFieldDisplay rather than inside a component.

/** Longest example string rendered before it is cut — a preview, not the data. */
const EXAMPLE_MAX_CHARS = 28;

/** How many examples a row shows; the remainder is reported as a count. */
const EXAMPLE_MAX_SHOWN = 3;

export interface ExampleValuesSpec {
  /** Formatted, length-capped previews — empty when examples are withheld or absent. */
  readonly shown: readonly string[];
  /** How many were captured in total, so the row can say "+N more". */
  readonly total: number;
  /** True when examples exist but are deliberately not shown (sensitive field). */
  readonly withheld: boolean;
}

/** One captured value as a single-line preview. Objects/arrays are JSON-shaped rather
 *  than "[object Object]"; a captured null IS informative, so it renders as `null`. */
export function formatExampleValue(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return '—';
  const text = typeof value === 'string' ? value : JSON.stringify(value) ?? String(value);
  return text.length > EXAMPLE_MAX_CHARS ? `${text.slice(0, EXAMPLE_MAX_CHARS - 1)}…` : text;
}

/**
 * What (if anything) a review row should show of a field's captured example values.
 *
 * Sensitive fields show NOTHING: the classifier already knows which fields carry PII,
 * credentials, financial or health data, and a real customer value is exactly what must
 * not be splashed across a review screen — the withheld flag lets the row say so.
 */
export function resolveExampleValues(field: InferredField): ExampleValuesSpec {
  const captured = field.exampleValues ?? [];
  const display = resolveFieldDisplay(field);
  // The class, not the row emphasis: an `identifier`-role tax number keeps identifier emphasis
  // but is still PII, and its values must stay hidden.
  const sensitive = display.sensitivity !== undefined || display.lock;
  if (captured.length === 0) return { shown: [], total: 0, withheld: false };
  if (sensitive) return { shown: [], total: captured.length, withheld: true };
  return {
    shown: captured.slice(0, EXAMPLE_MAX_SHOWN).map(formatExampleValue),
    total: captured.length,
    withheld: false,
  };
}
